import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, ScanLine, Settings2, Volume2, VolumeX, X } from 'lucide-react';
import { loadLaboratoryScanners, loadLaboratoryWork, receiveLaboratorySample, resolveLaboratoryCode, XomicsApiError } from './api';
import { useXomicsAuth } from './AuthContext';
import LaboratoryCollectionModal from './LaboratoryCollectionModal';
import { labError } from './LaboratoryParts';
import { LabScanContext, scanBuffer } from './labScanContext';
import { ownSampleTypeName, stageName } from './LaboratoryText';
import { XomicsSelect } from './parts';
import { tx } from './i18n';
import './laboratory-scan.css';

const text = (key, values) => tx(`ui.lab.scan.${key}`, values);
const SCANNER_KEY = 'xomics-lab-scanner';
const SOUND_KEY = 'xomics-lab-scan-sound';
const stored = (key, fallback) => { try { const value = window.localStorage.getItem(key); return value === null ? fallback : value; } catch (error) { return fallback; } };
const store = (key, value) => { try { window.localStorage.setItem(key, value); } catch (error) { /* kept for this visit only */ } };
// Fields a scanner never means to fill: a scan typed into one is taken back out and looked up.
const ROUTED_TYPES = new Set(['search', 'number', 'date', 'time', 'checkbox', 'radio', 'range', 'file', 'color', 'button', 'submit']);
const QUIET_MS = 6000;

function snapshot() {
  const element = document.activeElement;
  if (!element || element === document.body) return { element: null, kind: null };
  if (element.isContentEditable) return { element: null, kind: 'text' };
  if (!['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)) return { element: null, kind: null };
  const routed = element.tagName === 'SELECT' || element.closest('[data-scan-route]') !== null
    || (element.tagName === 'INPUT' && ROUTED_TYPES.has((element.getAttribute('type') || 'text').toLowerCase()));
  return { element, value: element.value, kind: routed ? 'field' : 'text' };
}

// Takes the scanned characters back out of the field they were typed into.
function restore(origin) {
  if (!origin || !origin.element || !origin.element.isConnected || origin.element.value === origin.value) return;
  const element = origin.element;
  const owner = element.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement : element.tagName === 'SELECT' ? window.HTMLSelectElement : window.HTMLInputElement;
  Object.getOwnPropertyDescriptor(owner.prototype, 'value').set.call(element, origin.value);
  element.dispatchEvent(new Event(element.tagName === 'SELECT' ? 'change' : 'input', { bubbles: true }));
}

// Where a tube stands when nothing is left to record for it here.
function standing(tube, orders) {
  if (tube.state === 'awaiting_acceptance') return text('next_acceptance');
  if (orders.length === 0) return ['accepted', 'accepted_conditional'].includes(tube.state) ? text('next_workflow') : tx(`ui.lab.state.${tube.state}`);
  if (orders.some(order => order.status === 'awaiting_approval')) return tx('ui.lab.stage.order.awaiting_approval');
  if (orders.some(order => order.status === 'awaiting_customer')) return tx('ui.lab.stage.order.awaiting_customer');
  return tx(`ui.lab.stage.order.${orders[orders.length - 1].status}`);
}

// Listens for scans on every laboratory page and takes a scanned tube to its
// next step: received if it was not, then its acceptance, its workflow, or the
// stage of lab work it is waiting at.
export default function LabScanStation({ active, children }) {
  const { permissions } = useXomicsAuth();
  const navigate = useNavigate();
  const entries = useRef([]);
  const confirmations = useRef(new Map());
  const sequence = useRef(0), counter = useRef(0), audio = useRef(null), waiting = useRef(null);
  const [desk, setDesk] = useState(null);
  const [scanner, setScanner] = useState(() => stored(SCANNER_KEY, ''));
  const [sound, setSound] = useState(() => stored(SOUND_KEY, 'on') === 'on');
  const [hud, setHud] = useState(null), [panel, setPanel] = useState(false), [typed, setTyped] = useState('');
  const [modal, setModal] = useState(null);
  const state = useRef({ scanner, sound, desk });
  state.current = { scanner, sound, desk };

  useEffect(() => {
    if (!active || desk !== null) return undefined;
    let current = true;
    loadLaboratoryScanners().then((answer) => {
      if (!current) return;
      setDesk(answer);
      // One scanner at this laboratory is the scanner; a remembered one that left is forgotten.
      const known = answer.scanners.some(item => item.deviceId === state.current.scanner);
      if (!known) { const only = answer.scanners.length === 1 ? answer.scanners[0].deviceId : ''; setScanner(only); store(SCANNER_KEY, only); }
    }).catch(() => { if (current) setDesk(false); });
    return () => { current = false; };
  }, [active, desk]);

  const beep = useCallback((good) => {
    if (!state.current.sound) return;
    try {
      const Audio = window.AudioContext || window.webkitAudioContext;
      if (!Audio) return;
      audio.current = audio.current || new Audio();
      const context = audio.current, oscillator = context.createOscillator(), gain = context.createGain();
      const length = good ? 0.12 : 0.34;
      oscillator.frequency.value = good ? 880 : 196;
      gain.gain.setValueAtTime(0.0001, context.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.14, context.currentTime + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + length);
      oscillator.connect(gain); gain.connect(context.destination);
      oscillator.start(); oscillator.stop(context.currentTime + length + 0.02);
    } catch (error) { /* a browser without sound still scans */ }
  }, []);

  const show = useCallback((result) => {
    counter.current += 1;
    setHud({ ...result, id: counter.current });
    if (!result.busy) beep(result.tone === 'ok');
  }, [beep]);

  useEffect(() => {
    if (hud === null || hud.busy || hud.tone !== 'ok') return undefined;
    const timer = window.setTimeout(() => setHud(current => (current && current.id === hud.id ? null : current)), QUIET_MS);
    return () => window.clearTimeout(timer);
  }, [hud]);

  const route = useCallback(async (raw, { force = false } = {}) => {
    const code = raw.trim();
    if (code === '') return;
    sequence.current += 1;
    const turn = sequence.current;
    const stale = () => turn !== sequence.current;
    show({ tone: 'info', code, title: text('working'), busy: true });
    try {
      const resolved = await resolveLaboratoryCode(code);
      if (stale()) return;
      const collection = resolved.collection;
      const kind = ownSampleTypeName(collection.sampleType);
      const tube = resolved.selectedTubeUuid === null ? null : collection.tubes.find(row => row.uuid === resolved.selectedTubeUuid) || null;
      // Shown as printed on a product kit (GH453369, GH453369-01); matching stays on the codes themselves.
      const shownCollection = collection.printedCode ?? collection.code;
      if (tube === null) {
        setModal({ uuid: collection.uuid, code: shownCollection });
        show({ tone: 'ok', code: shownCollection, title: text('choose_tube'), detail: kind });
        return;
      }
      const shown = tube.printedCode ?? tube.code;
      const forms = entries.current.map(entry => entry.current);
      const own = forms.find(entry => entry.tubeCode === tube.code && entry.onTube);
      if (own) {
        confirmations.current.set(tube.code, Date.now());
        own.onTube();
        show({ tone: 'ok', code: shown, title: text('confirmed'), detail: kind });
        return;
      }
      const unsaved = forms.find(entry => entry.dirty && entry.tubeCode && entry.tubeCode !== tube.code);
      if (unsaved && !force) {
        show({ tone: 'warn', code: shown, title: text('other_tube'), detail: text('other_tube_detail', { code: unsaved.tubeLabel ?? unsaved.tubeCode }),
          actions: [{ label: text('open_tube', { code: shown }), run: () => route(code, { force: true }) }] });
        return;
      }
      if (tube.state === 'awaiting_receipt') {
        const device = state.current.scanner, account = state.current.desk ? state.current.desk.accountId : null;
        if (device === '' || account === null) {
          waiting.current = code; setPanel(true);
          show({ tone: 'warn', code: shown, title: text('need_scanner'), detail: kind });
          return;
        }
        await receiveLaboratorySample({ sampleCode: tube.code, accountId: account, deviceId: device });
        if (stale()) return;
        confirmations.current.set(tube.code, Date.now());
        setModal({ uuid: collection.uuid, code: shownCollection });
        show({ tone: 'ok', code: shown, title: text('received'), detail: `${kind} · ${text('next_acceptance')}` });
        return;
      }
      confirmations.current.set(tube.code, Date.now());
      const work = await loadLaboratoryWork({ collectionUuid: collection.uuid, limit: 1 });
      if (stale()) return;
      const orders = work.collections.length === 0 ? [] : work.collections[0].workOrders.filter(order => order.tube.uuid === tube.uuid);
      const open = orders.find(order => order.canRecord && order.currentStage !== null);
      if (open) {
        setModal(null);
        navigate(`/xomics/laboratory/work?collection=${collection.uuid}`);
        show({ tone: 'ok', code: shown, title: `${open.workflow.code} · ${stageName(open.currentStage.code)}`, detail: kind });
        return;
      }
      setModal({ uuid: collection.uuid, code: shownCollection });
      show({ tone: 'ok', code: shown, title: standing(tube, orders), detail: kind });
    } catch (error) {
      if (stale()) return;
      const candidates = error instanceof XomicsApiError && error.code === 'SAMPLE_CODE_AMBIGUOUS' && error.body && Array.isArray(error.body.candidates)
        ? error.body.candidates : null;
      show({ tone: 'bad', code, title: labError(error),
        actions: candidates === null ? [] : candidates.map(candidate => ({ label: `${candidate.tubeCode} · ${candidate.organization.name}`, run: () => route(candidate.tubeCode) })) });
    }
  }, [navigate, show]);

  // Decided at once, on the scanner's Enter: a scan a form knows is its own;
  // text typed into a text field stays text; everything else is looked up.
  const take = useCallback((scan) => {
    const code = scan.code.trim();
    if (code === '') return false;
    const kind = scan.origin ? scan.origin.kind : null;
    for (const entry of entries.current) {
      const form = entry.current;
      // A form's own tube, scanned by its code or by its label as printed (GH453369-01).
      if (form.tubeCode && form.onTube && (form.tubeCode === code || form.tubeLabel === code)) {
        restore(scan.origin); confirmations.current.set(form.tubeCode, Date.now()); form.onTube();
        show({ tone: 'ok', code, title: text('confirmed') });
        return true;
      }
      const run = form.match ? form.match(code, kind) : null;
      if (run) {
        restore(scan.origin);
        Promise.resolve().then(run).then((result) => { if (result) show({ code, ...result }); })
          .catch(error => show({ tone: 'bad', code, title: labError(error) }));
        return true;
      }
    }
    if (kind === 'text') return false;
    restore(scan.origin);
    route(code);
    return true;
  }, [route, show]);

  useEffect(() => {
    if (!active || !desk) return undefined;
    const buffer = scanBuffer();
    const onKey = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) { buffer.reset(); return; }
      const scan = buffer.key(event.key, event.timeStamp, snapshot);
      if (scan !== null && take(scan)) { event.preventDefault(); event.stopPropagation(); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [active, desk, take]);

  const chooseScanner = useCallback((value) => {
    setScanner(value); store(SCANNER_KEY, value);
    state.current = { ...state.current, scanner: value };
    if (value !== '' && waiting.current !== null) { const code = waiting.current; waiting.current = null; route(code); }
  }, [route]);

  const station = useMemo(() => ({
    scanner,
    chooseScanner,
    // Runs `run` unless another tube has unsaved entries on screen; then it asks first.
    guarded(code, run) {
      const unsaved = entries.current.map(entry => entry.current).find(entry => entry.dirty && entry.tubeCode && entry.tubeCode !== code);
      if (!unsaved) return run();
      show({ tone: 'warn', code, title: text('other_tube'), detail: text('other_tube_detail', { code: unsaved.tubeLabel ?? unsaved.tubeCode }),
        actions: [{ label: text('open_tube', { code }), run }] });
      return null;
    },
    register(entry) {
      entries.current = [...entries.current, entry];
      return {
        remove() { entries.current = entries.current.filter(item => item !== entry); },
        touch() { if (entries.current[0] !== entry) entries.current = [entry, ...entries.current.filter(item => item !== entry)]; },
      };
    },
    // Whether this tube was scanned a moment ago, on the way to the form that asks.
    wasScanned(code) { const at = confirmations.current.get(code); return at !== undefined && Date.now() - at < 120000; },
    scan: route,
    announce: show,
  }), [chooseScanner, route, scanner, show]);

  const tone = hud === null ? 'idle' : hud.tone;
  const chosen = desk ? desk.scanners.find(item => item.deviceId === scanner) : null;
  return <LabScanContext.Provider value={active && desk ? station : null}>
    {children}
    {active && desk && <>
      {/* Room at the end of the page, so the station never sits on the last button. */}
      <div className="xo-scan-room" aria-hidden="true" />
      {modal !== null && <LaboratoryCollectionModal key={modal.uuid} collectionUuid={modal.uuid} code={modal.code} onClose={() => setModal(null)} onAssigned={() => {}}
        mayTrack={permissions.includes('SAMPLE_TRACK_READ') || permissions.includes('SAMPLE_TRACK_READ_ALL')} />}
      <aside className={`xo-scan-hud is-${tone}`} aria-label={text('title')}>
        {hud !== null && <div className="xo-scan-result" role="status" aria-live="polite">
          <span className="xo-scan-result-mark">{hud.busy ? <ScanLine className="xo-spin-slow" aria-hidden="true" /> : hud.tone === 'ok' ? <Check aria-hidden="true" />
            : <AlertTriangle aria-hidden="true" />}</span>
          <span className="xo-scan-result-text"><code>{hud.code}</code><strong>{hud.title}</strong>{hud.detail && <small>{hud.detail}</small>}</span>
          {!hud.busy && <button type="button" className="xo-icon-btn" aria-label={text('dismiss')} onClick={() => setHud(null)}><X aria-hidden="true" /></button>}
          {hud.actions && hud.actions.length > 0 && <span className="xo-scan-result-actions">{hud.actions.map(action => <button type="button" className="xo-btn-2"
            key={action.label} onClick={() => { setHud(null); action.run(); }}>{action.label}</button>)}</span>}
        </div>}
        {panel && <div className="xo-scan-panel">
          <p>{text('hint')}</p>
          <label><span>{text('scanner')}</span>
            <XomicsSelect value={scanner} onChange={event => chooseScanner(event.target.value)} aria-label={text('scanner')}>
              <option value="">{text('choose_scanner')}</option>
              {desk.scanners.map(item => <option key={item.deviceId} value={item.deviceId}>{item.deviceId} · {item.manufacturer} {item.model}</option>)}
            </XomicsSelect></label>
          <form onSubmit={(event) => { event.preventDefault(); if (typed.trim() !== '') { route(typed); setTyped(''); } }}>
            <label><span>{text('type_code')}</span>
              <input value={typed} onChange={event => setTyped(event.target.value)} maxLength={64} data-scan-route="" autoComplete="off" spellCheck="false" /></label>
            <button type="submit" className="xo-btn" disabled={typed.trim() === ''}>{text('open')}</button>
          </form>
          <button type="button" className="xo-scan-sound" aria-pressed={sound} onClick={() => { setSound(!sound); store(SOUND_KEY, sound ? 'off' : 'on'); }}>
            {sound ? <Volume2 aria-hidden="true" /> : <VolumeX aria-hidden="true" />}{text(sound ? 'sound_on' : 'sound_off')}</button>
        </div>}
        <button type="button" className="xo-scan-chip" aria-expanded={panel} onClick={() => setPanel(!panel)}>
          <ScanLine aria-hidden="true" />
          <span><strong>{text(chosen ? 'ready' : 'choose_scanner')}</strong>{chosen && <small>{chosen.deviceId} · {chosen.model}</small>}</span>
          <Settings2 aria-hidden="true" />
        </button>
      </aside>
    </>}
  </LabScanContext.Provider>;
}
